import {
  DISPLAY_CURRENCIES,
  LARGE_MOBILE,
  MOBILE_MENU,
  SMALL_SCREEN,
  TIME_RANGE_1_DAY,
} from "common/constants";
import Title from "common/Typography/Title";
import React from "react";
import { useTheme } from "styled-components";
import Col from "antd/es/col";
import AntRow from "antd/es/row";
import styled from "styled-components";
import { useSelector } from "react-redux";
import AccountTableChart from "features/portfolio/Accounts/AccountTableChart";
import useMedia from "utils/hooks/useMedia";
import PriceChange from "common/Charts/PriceChange";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import TextWithIcon from "common/Text/TextWithIcon";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import { ICON_TYPES } from "@shared/common/constants";
import { PrecisionType, TopMarketsTableDataBySymbol } from "@shared/types";

interface TopMarketProps {
  symbol: TopMarketsTableDataBySymbol;
}

const TopMarket = ({ symbol }: TopMarketProps) => {
  const isMobile = useMedia([`(max-width: ${LARGE_MOBILE})`], [true], false);
  const isTablet = useMedia([`(max-width: ${MOBILE_MENU})`], [true], false);
  const isSmallScreen = useMedia(
    [`(max-width: ${SMALL_SCREEN})`],
    [true],
    false
  );

  const appTheme = useTheme();
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { getMinimumPrecision, getPrecision } = usePrecisions();
  const priceCoin = symbol.symbol?.split("/")[1];

  const pair = (
    <TextWithIcon
      text={symbol.symbol}
      coinOrExchangeName={symbol.symbol}
      iconType={ICON_TYPES.COIN}
      iconSize={"l"}
      style={{
        color: appTheme.colors.textColor,
        fontSize: isTablet
          ? appTheme.typography.fontSizes.S
          : appTheme.typography.fontSizes.M,
      }}
    />
  );

  const pricePrecision = getMinimumPrecision(
    PrecisionType.PRICE,
    symbol.symbol,
    symbol.exchanges
  );

  const lastPrice = (
    <TextWithPrecisionAndIcon
      text={symbol.price}
      precision={pricePrecision}
      iconType={ICON_TYPES.COIN}
      coinOrExchangeName={priceCoin}
      style={{
        color: appTheme.colors.textColor,
        fontSize: isSmallScreen
          ? appTheme.typography.fontSizes.S
          : appTheme.typography.fontSizes.M,
      }}
    />
  );

  const graphData = {
    [TIME_RANGE_1_DAY]: symbol.graph_data,
    change: symbol.change,
  };

  const highLowPrecision = getPrecision(
    displayCurrency === DISPLAY_CURRENCIES.USD
      ? PrecisionType.USD_PRICE
      : PrecisionType.BTC_PRICE
  );
  const highPrice = (
    <TextWithPrecisionAndIcon
      text={
        displayCurrency === DISPLAY_CURRENCIES.USD
          ? symbol.high_price[0]
          : symbol.high_price[1]
      }
      iconType={ICON_TYPES.DISPLAY_CURRENCY}
      precision={highLowPrecision}
    />
  );
  const lowPrice = (
    <TextWithPrecisionAndIcon
      text={
        displayCurrency === DISPLAY_CURRENCIES.USD
          ? symbol.low_price[0]
          : symbol.low_price[1]
      }
      iconType={ICON_TYPES.DISPLAY_CURRENCY}
      precision={highLowPrecision}
    />
  );

  const volumePrecision = getPrecision(
    displayCurrency === DISPLAY_CURRENCIES.USD
      ? PrecisionType.USD_AMOUNT
      : PrecisionType.BTC_AMOUNT
  );
  const volume = (
    <TextWithPrecisionAndIcon
      text={
        displayCurrency === DISPLAY_CURRENCIES.USD
          ? symbol.volume[0]
          : symbol.volume[1]
      }
      iconType={ICON_TYPES.DISPLAY_CURRENCY}
      precision={volumePrecision}
    />
  );

  return (
    <Row>
      <Col
        span={isMobile ? 9 : isSmallScreen ? 4 : 5}
        style={{ paddingRight: 6 }}
      >
        {pair}
      </Col>
      <Col span={isMobile ? 9 : isSmallScreen ? 4 : 5}>{lastPrice}</Col>
      <Col
        span={isMobile ? 6 : isSmallScreen ? 4 : 5}
        style={{
          display: "flex",
          justifyContent: isSmallScreen ? "center" : "start",
        }}
      >
        <ChangeWrapper $isMobile={isTablet}>
          {isTablet ? (
            <PriceChange
              change={symbol.change}
              size={appTheme.typography.pxFontSizes.S}
              useBackground={true}
            />
          ) : (
            <AccountTableChart
              data={graphData}
              isPrice={true}
              yValueIndex={1}
              fontSize={appTheme.typography.pxFontSizes.XS}
              chartHeight={35}
            />
          )}
        </ChangeWrapper>
      </Col>
      {!isMobile && (
        <>
          <Col
            span={isSmallScreen ? 7 : 6}
            style={{ textAlign: isSmallScreen ? "center" : "start" }}
          >
            <Title level={5}>
              {lowPrice}
              {highPrice && lowPrice && <span>&nbsp;/&nbsp;</span>}
              {highPrice}
            </Title>
          </Col>
          <Col
            span={isSmallScreen ? 5 : 3}
            style={{ textAlign: isSmallScreen ? "end" : "start" }}
          >
            <Title level={5}>{volume}</Title>
          </Col>
        </>
      )}
    </Row>
  );
};

export default TopMarket;

const Row = styled(AntRow)`
  height: 48px;
  display: flex;
  align-items: center;
`;
const ChangeWrapper = styled.div<{ $isMobile: boolean }>`
  width: ${(p) => (p.$isMobile ? "fit-content" : "100px")};
`;
